import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button as MuiButton,
  Chip,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';
import { useNotifications } from '@/shared/ui/Notifications';

const API_BASE_URL = 'https://api.letsgochat.ru/api/public/v1';
const OPENAPI_URL = `${API_BASE_URL}/openapi.yaml`;

const endpoints = [
  {
    method: 'GET',
    path: '/me',
    scope: '*',
    titleKey: 'me',
  },
  {
    method: 'GET',
    path: '/channels',
    scope: 'channels:read',
    titleKey: 'channels',
  },
  {
    method: 'GET',
    path: '/channels/{channelId}/contacts',
    scope: 'contacts:read',
    titleKey: 'contacts',
  },
  {
    method: 'GET',
    path: '/channels/{channelId}/chats',
    scope: 'chats:read',
    titleKey: 'chats',
  },
  {
    method: 'GET',
    path: '/channels/{channelId}/chats/{chatId}/messages',
    scope: 'messages:read',
    titleKey: 'messages',
  },
  {
    method: 'POST',
    path: '/channels/{channelId}/chats/{chatId}/messages',
    scope: 'messages:write',
    titleKey: 'sendMessage',
  },
  {
    method: 'GET',
    path: '/channels/{channelId}/webhooks',
    scope: 'webhooks:write',
    titleKey: 'listWebhooks',
  },
  {
    method: 'PUT',
    path: '/channels/{channelId}/webhooks',
    scope: 'webhooks:write',
    titleKey: 'saveWebhook',
  },
  {
    method: 'DELETE',
    path: '/channels/{channelId}/webhooks/{webhookId}',
    scope: 'webhooks:write',
    titleKey: 'deleteWebhook',
  },
] as const;

const scopes = [
  { value: 'channels:read', key: 'channelsRead' },
  { value: 'contacts:read', key: 'contactsRead' },
  { value: 'chats:read', key: 'chatsRead' },
  { value: 'messages:read', key: 'messagesRead' },
  { value: 'messages:write', key: 'messagesWrite' },
  { value: 'webhooks:write', key: 'webhooksWrite' },
  { value: '*', key: 'fullAccess' },
] as const;

const codeSx = {
  bgcolor: '#111827',
  borderRadius: '12px',
  color: 'common.white',
  fontFamily: 'monospace',
  fontSize: 13,
  lineHeight: 1.6,
  overflowX: 'auto',
  p: 1.5,
  whiteSpace: 'pre',
};

function CodeBlock({
  value,
  onCopy,
}: {
  value: string;
  onCopy: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={{ position: 'relative' }}>
      <Box component="pre" sx={codeSx}>
        {value}
      </Box>
      <Tooltip title={t('integrations:publicApi.docs.copyCode')}>
        <Button
          size="small"
          variant="text"
          startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={() => onCopy(value)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.08)',
            color: 'common.white',
            position: 'absolute',
            right: 8,
            top: 8,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
          }}
        >
          {t('common:buttons.copy')}
        </Button>
      </Tooltip>
    </Box>
  );
}

export function PublicApiDocumentation() {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();

  const copyText = async (value: string) => {
    await navigator.clipboard.writeText(value);
    addNotification({
      type: 'success',
      title: t('integrations:publicApi.copied'),
    });
  };

  const listChannelsExample = `curl -X GET "${API_BASE_URL}/channels" \\
  -H "Authorization: Bearer <api_key>"`;

  const sendMessageExample = `curl -X POST "${API_BASE_URL}/channels/<channelId>/chats/<chatId>/messages" \\
  -H "Authorization: Bearer <api_key>" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-123-message-1" \\
  -d '{
    "text": "Hello from CRM.",
    "message_type": "text"
  }'`;

  const webhookExample = `const crypto = require('crypto');

function verifyGoChatWebhook(req, signingSecret) {
  const timestamp = req.header('X-GoChat-Timestamp');
  const signature = req.header('X-GoChat-Signature');
  const rawBody = req.rawBody;
  const expected = crypto
    .createHmac('sha256', signingSecret)
    .update(\`\${timestamp}.\${rawBody}\`, 'utf8')
    .digest('hex');

  return signature === \`sha256=\${expected}\`;
}`;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Box sx={{ flex: 1 }}>
          <CustomTypography variant="h4" sx={{ fontSize: 20, fontWeight: 600 }}>
            {t('integrations:publicApi.docs.title')}
          </CustomTypography>
          <CustomTypography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {t('integrations:publicApi.docs.caption')}
          </CustomTypography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={() => copyText(API_BASE_URL)}
            sx={{ borderRadius: '12px' }}
          >
            {t('integrations:publicApi.docs.copyBaseUrl')}
          </Button>
          <MuiButton
            disableFocusRipple
            disableRipple
            href={OPENAPI_URL}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon fontSize="small" />}
            sx={{ borderRadius: '12px', textDecoration: 'none' }}
          >
            OpenAPI
          </MuiButton>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        {['createKey', 'sendBearer', 'useWebhook'].map((step, index) => (
          <Paper
            key={step}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'custom.grey6',
              borderRadius: '14px',
              flex: 1,
              p: 1.5,
            }}
          >
            <Chip size="small" label={index + 1} sx={{ mb: 1 }} />
            <CustomTypography sx={{ fontWeight: 600 }}>
              {t(`integrations:publicApi.docs.steps.${step}.title`)}
            </CustomTypography>
            <CustomTypography sx={{ color: 'text.secondary', fontSize: 14 }}>
              {t(`integrations:publicApi.docs.steps.${step}.caption`)}
            </CustomTypography>
          </Paper>
        ))}
      </Stack>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'custom.grey6',
          borderRadius: '14px !important',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CustomTypography sx={{ fontWeight: 600 }}>
            {t('integrations:publicApi.docs.sections.examples')}
          </CustomTypography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <CodeBlock value={listChannelsExample} onCopy={copyText} />
            <CodeBlock value={sendMessageExample} onCopy={copyText} />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'custom.grey6',
          borderRadius: '14px !important',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CustomTypography sx={{ fontWeight: 600 }}>
            {t('integrations:publicApi.docs.sections.endpoints')}
          </CustomTypography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={0.75}>
            {endpoints.map((endpoint) => (
              <Stack
                key={`${endpoint.method}-${endpoint.path}`}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{
                  border: '1px solid',
                  borderColor: 'custom.grey6',
                  borderRadius: '12px',
                  p: 1,
                }}
              >
                <Chip
                  label={endpoint.method}
                  size="small"
                  color={endpoint.method === 'GET' ? 'default' : 'primary'}
                  sx={{ minWidth: 64 }}
                />
                <CustomTypography
                  component="code"
                  sx={{
                    flex: 1,
                    fontFamily: 'monospace',
                    fontSize: 13,
                    wordBreak: 'break-all',
                  }}
                >
                  {endpoint.path}
                </CustomTypography>
                <Chip label={endpoint.scope} size="small" variant="outlined" />
                <CustomTypography sx={{ flex: 1, fontSize: 14 }}>
                  {t(
                    `integrations:publicApi.docs.endpoints.${endpoint.titleKey}`
                  )}
                </CustomTypography>
              </Stack>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'custom.grey6',
          borderRadius: '14px !important',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CustomTypography sx={{ fontWeight: 600 }}>
            {t('integrations:publicApi.docs.sections.scopes')}
          </CustomTypography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {scopes.map((scope) => (
              <Chip
                key={scope.value}
                label={`${scope.value} — ${t(`integrations:publicApi.docs.scopes.${scope.key}`)}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'custom.grey6',
          borderRadius: '14px !important',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CustomTypography sx={{ fontWeight: 600 }}>
            {t('integrations:publicApi.docs.sections.webhookSignature')}
          </CustomTypography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <CustomTypography sx={{ color: 'text.secondary', fontSize: 14 }}>
              {t('integrations:publicApi.docs.webhookSignatureText')}
            </CustomTypography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {[
                'X-GoChat-Event',
                'X-GoChat-Delivery',
                'X-GoChat-Timestamp',
                'X-GoChat-Signature',
              ].map((header) => (
                <Chip key={header} label={header} size="small" />
              ))}
            </Stack>
            <CodeBlock value={webhookExample} onCopy={copyText} />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
